import React, { memo, useCallback, useMemo } from "react"
import dynamic from "next/dynamic"

import {
  reviewAssigneeMatches,
  useHumanFeedbackUpdate,
  useReviewAnnotationMode,
  useReviewSubjectAnnotation,
} from "@/human-feedback/annotations"
import { useMetricRecordMetadataById } from "@/metrics/transport-data"

import type { Annotation } from "@/types/api/Annotation"
import type { MetricRecordData } from "@/types/metrics/contracts"
import type { RunMetricOutput } from "@/types/RunMetricOutput"

import { RubricDecisionTreeConfig } from "../../configuration/components/RubricDecisionTreeConfig"
import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { NumericalHumanFeedbackButton } from "../helpers/NumericalHumanFeedbackButton"
import {
  DecisionTreeScoreInput,
  FailureList,
  getReviewJson,
  HumanReviewInstructionsDisplay,
  isDecisionTreeResult,
  NodeDetailTable,
  type DecisionTreeReviewJson,
} from "./DecisionTreeReviewDetails"
import { useDecisionTreeNodeToggle } from "./useDecisionTreeNodeToggle"

const RubricDecisionTreeFlow = dynamic(
  () =>
    import("./RubricDecisionTreeFlow").then((mod) => ({
      default: mod.RubricDecisionTreeFlow,
    })),
  { ssr: false },
)

// ============================================================================
// CARD-LEVEL ANNOTATIONS PANEL: score input shown at the 50% point of the card
// ============================================================================

interface DecisionTreeCardAnnotationsPanelProps {
  metricId: string
  simulationOutputId: string
  annotationsForMetric: Annotation[]
  projectId?: string
}

const DecisionTreeCardAnnotationsPanel: React.FC<DecisionTreeCardAnnotationsPanelProps> =
  memo(
    ({
      metricId,
      simulationOutputId,
      annotationsForMetric,
      projectId,
    }: DecisionTreeCardAnnotationsPanelProps) => {
      const { isReadOnly, subjectEmail: userEmail } = useReviewAnnotationMode()
      const myAnnotation = useReviewSubjectAnnotation(annotationsForMetric)

      const otherAnnotations = useMemo(
        () =>
          annotationsForMetric.filter(
            (annotation: Annotation) =>
              !reviewAssigneeMatches(annotation.assignee, userEmail),
          ),
        [annotationsForMetric, userEmail],
      )

      const { currentAnnotation, handleValueUpdate } = useHumanFeedbackUpdate({
        simulationOutputId,
        metricId,
        initialAnnotation: myAnnotation || null,
        projectId,
      })

      const handleValueChange = useCallback(
        (
          floatValue: number | null,
          _stringValue: string | null,
          _setValue: string[] | null,
        ) => {
          if (isReadOnly) {
            return
          }
          const current = getReviewJson(currentAnnotation)
          const updatedJson: DecisionTreeReviewJson = {
            visited_nodes: current.visited_nodes,
            score: floatValue,
          }
          void handleValueUpdate(floatValue, null, null, updatedJson)
        },
        [currentAnnotation, handleValueUpdate, isReadOnly],
      )

      return (
        <NumericalHumanFeedbackButton
          annotation={currentAnnotation}
          onValueChange={handleValueChange}
          onExplanationChange={() => {}}
          otherUserAnnotations={otherAnnotations}
          minValue={1}
          maxValue={5}
          readOnly={isReadOnly}
        />
      )
    },
  )
DecisionTreeCardAnnotationsPanel.displayName =
  "DecisionTreeCardAnnotationsPanel"

interface DecisionTreeSubRowsProps {
  metricId: string
  simulationOutputId: string
  annotationsForMetric: Annotation[]
  projectId?: string
  metricOutput?: RunMetricOutput
}

const DecisionTreeSubRows: React.FC<DecisionTreeSubRowsProps> = memo(
  ({
    metricId,
    simulationOutputId,
    annotationsForMetric,
    projectId,
    metricOutput,
  }) => {
    const { isReadOnly } = useReviewAnnotationMode()
    const myAnnotation = useReviewSubjectAnnotation(annotationsForMetric)

    const { currentAnnotation, handleValueUpdate } = useHumanFeedbackUpdate({
      simulationOutputId,
      metricId,
      initialAnnotation: myAnnotation || null,
      projectId,
    })

    const { metricRecordsById } = useMetricRecordMetadataById()
    const instructions =
      metricRecordsById[metricId]?.metric_metadata?.human_review_instructions

    const resultJson: unknown = metricOutput?.result?.raw_values
    const { visitedNodes, toggleNode } = useDecisionTreeNodeToggle({
      currentAnnotation,
      isReadOnly,
      handleValueUpdate,
    })

    const markedNodeIds = useMemo(() => new Set(visitedNodes), [visitedNodes])

    return (
      <tr className="border-b border-coval-border/50 bg-surface-1">
        <td colSpan={4} className="py-2 pl-8 pr-4">
          <HumanReviewInstructionsDisplay instructions={instructions} />
          {isDecisionTreeResult(resultJson) && (
            <div className="flex flex-col gap-3">
              {/* Summary */}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-coval-text-muted">
                <span>
                  Score:{" "}
                  <span className="font-medium text-coval-text">
                    {resultJson.score}
                  </span>
                </span>
                <span>
                  Intent expressed:{" "}
                  <span className="font-medium text-coval-text">
                    {resultJson.intent_expressed ? "Yes" : "No"}
                  </span>
                </span>
                <span>
                  Tree triggered:{" "}
                  <span className="font-medium text-coval-text">
                    {resultJson.tree_triggered ? "Yes" : "No"}
                  </span>
                </span>
                {resultJson.diverged_at_node && (
                  <span>
                    Diverged at:{" "}
                    <span className="font-medium text-coval-danger-text">
                      {resultJson.diverged_at_node}
                    </span>
                  </span>
                )}
              </div>
              {/* Node details */}
              {resultJson.nodes_visited.length > 0 && (
                <div className="space-y-1">
                  <h4 className="text-xs font-semibold text-coval-text-muted">
                    Nodes Visited
                  </h4>
                  <div className="overflow-x-auto rounded-lg border border-coval-border">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-surface-default/50 border-b border-coval-border">
                          <th className="text-left px-3 py-1.5 font-medium text-coval-text-muted">
                            Node
                          </th>
                          <th className="text-left px-3 py-1.5 font-medium text-coval-text-muted">
                            Expected
                          </th>
                          <th className="text-left px-3 py-1.5 font-medium text-coval-text-muted">
                            Actual
                          </th>
                          <th className="text-center px-3 py-1.5 font-medium text-coval-text-muted">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {resultJson.nodes_visited.map((node, i) => (
                          <tr
                            key={`${node.node_id}-${i}`}
                            className="border-b border-coval-border last:border-b-0"
                          >
                            <td className="px-3 py-1.5 font-medium text-coval-text whitespace-nowrap">
                              {node.node_id}
                            </td>
                            <td className="px-3 py-1.5 text-coval-text-muted max-w-[200px]">
                              {node.expected_bot_response}
                            </td>
                            <td className="px-3 py-1.5 text-coval-text-muted max-w-[200px]">
                              {node.actual_bot_response}
                            </td>
                            <td className="px-3 py-1.5 text-center">
                              {node.correct ? (
                                <span className="text-coval-success-text font-medium">
                                  Pass
                                </span>
                              ) : (
                                <span className="text-coval-danger-text font-medium">
                                  Fail
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Failures */}
              {resultJson.critical_failures.length > 0 && (
                <div className="space-y-1">
                  <h4 className="text-xs font-semibold text-coval-danger-text">
                    Critical Failures ({resultJson.critical_failures.length})
                  </h4>
                  <div className="space-y-1">
                    {resultJson.critical_failures.map((f, i) => (
                      <div
                        key={`${f.node_id}-${i}`}
                        className="text-xs bg-coval-danger-surface rounded p-2 border border-coval-danger-border"
                      >
                        <span className="font-medium text-coval-text">
                          {f.node_id}:
                        </span>{" "}
                        <span className="text-coval-text-muted">
                          {f.failure}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {resultJson.non_critical_failures.length > 0 && (
                <div className="space-y-1">
                  <h4 className="text-xs font-semibold text-coval-warning-text">
                    Non-critical Issues (
                    {resultJson.non_critical_failures.length})
                  </h4>
                  <div className="space-y-1">
                    {resultJson.non_critical_failures.map((f, i) => (
                      <div
                        key={`${f.node_id}-${i}`}
                        className="text-xs bg-coval-warning-surface rounded p-2 border border-coval-warning-border"
                      >
                        <span className="font-medium text-coval-text">
                          {f.node_id}:
                        </span>{" "}
                        <span className="text-coval-text-muted">
                          {f.failure}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tree flow for marking nodes */}
              <div className="border-t border-coval-border pt-2">
                <p className="text-xs text-coval-text-muted mb-2">
                  {isReadOnly
                    ? "Nodes visited during the conversation:"
                    : "Select each node that was visited during the conversation, in the graph or the outline:"}
                </p>
                <RubricDecisionTreeFlow
                  resultJson={resultJson}
                  onNodeClick={isReadOnly ? undefined : toggleNode}
                  markedNodeIds={markedNodeIds}
                />
                {markedNodeIds.size > 0 && (
                  <p className="text-xs text-coval-text-muted mt-2">
                    {markedNodeIds.size} node
                    {markedNodeIds.size !== 1 ? "s" : ""} marked as visited
                  </p>
                )}
              </div>
            </div>
          )}
        </td>
      </tr>
    )
  },
)
DecisionTreeSubRows.displayName = "DecisionTreeSubRows"

interface DecisionTreeReviewCellProps {
  metricId: string
  simulationOutputId: string
  annotationsForMetric: Annotation[]
  projectId?: string
}

const DecisionTreeReviewCell: React.FC<DecisionTreeReviewCellProps> = memo(
  ({ metricId, simulationOutputId, annotationsForMetric, projectId }) => {
    const { isReadOnly } = useReviewAnnotationMode()
    const myAnnotation = useReviewSubjectAnnotation(annotationsForMetric)

    const { currentAnnotation, handleValueUpdate } = useHumanFeedbackUpdate({
      simulationOutputId,
      metricId,
      initialAnnotation: myAnnotation || null,
      projectId,
    })

    const reviewJson = getReviewJson(currentAnnotation)

    const handleScoreChange = useCallback(
      (newScore: number | null) => {
        if (isReadOnly) {
          return
        }
        const current = getReviewJson(currentAnnotation)
        const updatedJson: DecisionTreeReviewJson = {
          visited_nodes: current.visited_nodes,
          score: newScore,
        }
        void handleValueUpdate(newScore, null, null, updatedJson)
      },
      [currentAnnotation, handleValueUpdate, isReadOnly],
    )

    const nodeCount = reviewJson.visited_nodes.length

    return (
      <div className="flex flex-col items-center gap-1">
        <DecisionTreeScoreInput
          currentScore={reviewJson.score ?? null}
          onScoreChange={handleScoreChange}
          readOnly={isReadOnly}
        />
        {nodeCount > 0 && (
          <span className="text-[10px] text-coval-text-muted">
            {nodeCount} node{nodeCount !== 1 ? "s" : ""}
          </span>
        )}
      </div>
    )
  },
)
DecisionTreeReviewCell.displayName = "DecisionTreeReviewCell"

export class RubricDecisionTreeMetricRecord extends NumericalMetricRecord {
  private static readonly METRIC_TYPE_LABEL = "Rubric Decision Tree"

  _renderDeepDive(
    mo: RunMetricOutput,
    _testCase: unknown,
    dispatchMessageSelect?: (messageIndex: number | null) => void,
  ): React.ReactNode {
    const resultJson: unknown = mo.result?.raw_values
    if (!isDecisionTreeResult(resultJson)) {
      return (
        <div className="text-sm text-coval-text">
          <strong>Result</strong>: {mo.float_value ?? "N/A"}
        </div>
      )
    }

    // Without a selection target, rows and failures render as plain content.
    const handleMessageClick = dispatchMessageSelect
      ? (messageIndex: number) => dispatchMessageSelect(messageIndex)
      : undefined

    return (
      <div className="space-y-4">
        <NodeDetailTable
          nodesVisited={resultJson.nodes_visited}
          onMessageClick={handleMessageClick}
        />

        <FailureList
          title="Critical Failures"
          failures={resultJson.critical_failures}
          variant="danger"
          onMessageClick={handleMessageClick}
        />

        <FailureList
          title="Non-critical Issues"
          failures={resultJson.non_critical_failures}
          variant="warning"
          onMessageClick={handleMessageClick}
        />

        <RubricDecisionTreeFlow resultJson={resultJson} />
      </div>
    )
  }

  getHighlightedMessages(mo: RunMetricOutput): number[] {
    const resultJson: unknown = mo.result?.raw_values
    if (!isDecisionTreeResult(resultJson)) {
      return []
    }

    const indices = new Set<number>()
    for (const node of resultJson.nodes_visited) {
      indices.add(node.message_index)
    }
    for (const failure of resultJson.critical_failures) {
      indices.add(failure.message_index)
    }
    for (const failure of resultJson.non_critical_failures) {
      indices.add(failure.message_index)
    }

    const sorted = [...indices].sort((a, b) => a - b)
    return sorted.length < 20 ? sorted : []
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
  ): React.ReactNode {
    return (
      <RubricDecisionTreeConfig
        metricRecordData={metricRecordData}
        onChange={onChange}
      />
    )
  }

  hasCustomVotingUI = true

  renderDeepDiveAnnotationsPanel(
    simulationOutputId: string,
    annotationsForMetric: Annotation[],
  ): React.ReactNode {
    return (
      <DecisionTreeCardAnnotationsPanel
        metricId={this.id || ""}
        simulationOutputId={simulationOutputId}
        annotationsForMetric={annotationsForMetric}
      />
    )
  }

  renderDeepDivePanel(
    notesNode: React.ReactNode,
    metricOutput: RunMetricOutput | undefined,
    onMessageSelect?: (messageIndex: number | null) => void,
  ): React.ReactNode {
    if (!metricOutput) {
      return null
    }
    const resultJson: unknown = metricOutput.result?.raw_values
    if (!isDecisionTreeResult(resultJson)) {
      return null
    }

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <div className="min-w-0 space-y-4">
            <NodeDetailTable
              nodesVisited={resultJson.nodes_visited}
              onMessageClick={onMessageSelect}
            />
            <FailureList
              title="Critical Failures"
              failures={resultJson.critical_failures}
              variant="danger"
              onMessageClick={onMessageSelect}
            />
            <FailureList
              title="Non-critical Issues"
              failures={resultJson.non_critical_failures}
              variant="warning"
              onMessageClick={onMessageSelect}
            />
          </div>
          <div className="min-w-0">{notesNode}</div>
        </div>
        <RubricDecisionTreeFlow resultJson={resultJson} />
      </div>
    )
  }

  renderAssignmentsTableReviewCell(
    simulationOutputId: string,
    annotationsForMetric: Annotation[],
    projectId?: string,
  ): React.ReactNode {
    return (
      <DecisionTreeReviewCell
        metricId={this.id || ""}
        simulationOutputId={simulationOutputId}
        annotationsForMetric={annotationsForMetric}
        projectId={projectId}
      />
    )
  }

  renderAssignmentsTableSubRows(
    simulationOutputId: string,
    annotationsForMetric: Annotation[],
    projectId?: string,
    metricOutput?: RunMetricOutput,
  ): React.ReactNode {
    return (
      <DecisionTreeSubRows
        metricId={this.id || ""}
        simulationOutputId={simulationOutputId}
        annotationsForMetric={annotationsForMetric}
        projectId={projectId}
        metricOutput={metricOutput}
      />
    )
  }

  renderMetricType(): React.ReactNode {
    return RubricDecisionTreeMetricRecord.METRIC_TYPE_LABEL
  }

  renderMetadataField(metricRecordData: MetricRecordData): React.ReactNode {
    const treeDefinition = metricRecordData.metric_metadata?.tree_definition
    const nodeCount = Array.isArray(treeDefinition) ? treeDefinition.length : 0

    return (
      <div className="flex flex-col gap-2">
        <div>
          <h3 className="font-semibold">Decision Tree Prompt: </h3>
          <p className="text-sm text-coval-text-muted line-clamp-3">
            {metricRecordData.metric_metadata?.decision_tree_prompt ||
              "Not set"}
          </p>
        </div>
        <div>
          <h3 className="font-semibold">Tree Nodes: </h3>
          <p>{nodeCount}</p>
        </div>
        {metricRecordData.metric_metadata?.model_version && (
          <div>
            <h3 className="font-semibold">Model: </h3>
            <p>{metricRecordData.metric_metadata.model_version}</p>
          </div>
        )}
      </div>
    )
  }
}
